/**
 * Minimal history router. The original site was a set of separate .html files;
 * these routes replace them one-for-one:
 *
 *   index.html   -> /
 *   watch.html   -> /watch
 *   live.html    -> /live
 *   contact.html -> /contact
 *
 * A route is not necessarily what the address bar shows: the published site
 * sits under `/elsa/`, so this module is where routes become hrefs and back
 * again. Everything on either side of it deals in routes only — see
 * src/basePath.ts.
 */
import { type MouseEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { stripBase, withBase } from "./basePath";
import { warmRoute } from "./routePrewarm";

/** The current route, i.e. the address bar with the deploy base taken off. */
function currentRoute(): string {
  return stripBase(window.location.pathname);
}

export function usePathname(): string {
  const [pathname, setPathname] = useState(currentRoute);

  useEffect(() => {
    const onNavigate = () => setPathname(currentRoute());
    window.addEventListener("popstate", onNavigate);
    window.addEventListener("river:navigate", onNavigate);
    return () => {
      window.removeEventListener("popstate", onNavigate);
      window.removeEventListener("river:navigate", onNavigate);
    };
  }, []);

  return pathname;
}

export function navigate(to: string) {
  const href = withBase(to);
  if (href === window.location.pathname) return;
  window.history.pushState(null, "", href);
  window.dispatchEvent(new Event("river:navigate"));
  window.scrollTo(0, 0);
}

/**
 * An <a> that navigates client-side but still behaves like a real link. `to` is
 * a route; the rendered `href` is that route under the deploy base, so
 * middle-click, copy-link and reload all land on the page that was clicked.
 */
export function Link({
  to,
  children,
  ...rest
}: { to: string; children?: ReactNode } & Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
>) {
  // The idle sweep in routePrewarm normally gets there first. This covers the
  // first visit, where a quick click can beat it: by the time the pointer is
  // down we know where it is going, and a decode started now is a decode not
  // started during the navigation. Already-warm routes make it a Map lookup.
  const onIntent = useCallback(() => {
    void warmRoute(to, "high");
  }, [to]);

  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      // let the browser handle modified clicks (new tab, download, ...)
      if (
        e.defaultPrevented ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey ||
        e.button !== 0
      ) {
        return;
      }
      e.preventDefault();
      navigate(to);
    },
    [to],
  );

  return (
    <a
      href={withBase(to)}
      onClick={onClick}
      onPointerDown={onIntent}
      onPointerEnter={onIntent}
      onFocus={onIntent}
      {...rest}
    >
      {children}
    </a>
  );
}
